import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Page from 'components/layouts/page/Page';
import FilterChips from 'components/shared/FilterChips';
import { useProfile } from 'context/profile';
import usePagination from 'hooks/usePagination';
import { PreferredProviderListRow } from 'models/PreferredProviderList';
import { PERMISSIONS } from 'models/Profile';
import { indexPreferredProviderLists, preferredProviderListQueryKeys } from 'services/api/preferredProviderLists';
import { serializeSort } from 'stores/helpers';
import { usePreferredProvidersActions, usePreferredProvidersStore } from 'stores/preferredProvidersStore';
import { BodySmall } from 'styles/typography';

import FilterBar from './FilterBar';
import { selectablePreferredProviderTypes } from './helpers';
import PreferredProvidersSlideout from './PreferredProvidersSlideout';
import PreferredProvidersTable from './PreferredProvidersTable';

const PreferredProvidersPage = () => {
  const { profile } = useProfile();
  const pagination = usePagination();
  const filters = usePreferredProvidersStore((state) => state.filters);
  const search = usePreferredProvidersStore((state) => state.search);
  const sort = usePreferredProvidersStore((state) => state.sort);
  const { setFilters, setSearch, setSort, removeFilter } = usePreferredProvidersActions();
  const [selected, setSelected] = useState<PreferredProviderListRow | null>(null);

  useEffect(
    () => () => {
      setFilters({});
      setSearch('');
      setSort([{ id: 'locationName', desc: false }]);
    },
    [setFilters, setSearch, setSort]
  );

  // Comma-joined, like every other filter the web sends (serializeFilters in services/api). A string also
  // keeps the page-reset effect below from firing when FilterBar re-applies unchanged filters as a new
  // object, which would otherwise bounce the user back to page 1 on every apply.
  const groupId = Object.values(filters)
    .flatMap((values) => (values ?? []).map((option) => option.id))
    .filter((id): id is string => !!id)
    .join(',');

  // Search and filters change the result set, so start back on the first page.
  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, groupId]);

  const queryParams = useMemo(
    () => ({
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      search,
      groupId: groupId || undefined,
    }),
    [pagination.paging.page, pagination.paging.pageSize, sort, search, groupId]
  );

  const { data, isLoading } = useQuery({
    queryKey: preferredProviderListQueryKeys.index(queryParams),
    queryFn: ({ signal }) => indexPreferredProviderLists(queryParams, signal),
  });

  const handleOpen = useCallback((row: PreferredProviderListRow) => setSelected(row), []);

  return (
    <Page>
      <Page.Header>
        <FilterBar />
      </Page.Header>
      <Page.SubHeader>
        <SubHeader>
          <FilterChips filters={filters} onRemoveFilter={removeFilter} useStore={usePreferredProvidersStore} />
          <BodySmall>
            Manage your preferred providers in Olio to list them first and add a visual indicator in any relevant form
            fields.
          </BodySmall>
        </SubHeader>
      </Page.SubHeader>
      <Page.Content>
        <PreferredProvidersTable data={data} isLoading={isLoading} pagination={pagination} onOpen={handleOpen} />
      </Page.Content>
      {selected && (
        <PreferredProvidersSlideout
          // A fresh panel per location, and a closing panel's delayed close can't clear the next one.
          key={selected.locationId}
          row={selected}
          clientId={profile.actingClient.id}
          selectableTypeIds={selectablePreferredProviderTypes(profile).map((type) => type.id)}
          canEdit={profile.has(PERMISSIONS.preferredProviderEdit)}
          handleClose={() => setSelected((current) => (current?.locationId === selected.locationId ? null : current))}
        />
      )}
    </Page>
  );
};

const SubHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
`;

export default PreferredProvidersPage;
